import React from 'react'
import PropTypes from 'prop-types'
import { Tooltip, Button } from '@material-ui/core'
import classNames from 'classnames'
import ImageCarousel from './components/ImageCarousel'
import OwnWant from './components/OwnWant'
import UpDownVote from './components/UpDownVote'
import Img from './components/Img'
import SizingSelect from './components/SizingSelect'
import ClampedText from './components/ClampedText'
import HeaderStatistic from './components/HeaderStatistic'
import styles from './Item.icss'
import voteTypes from './enums/voteTypes'
import FeatureTranslate from './components/FeatureTranslate'
import FeatureDescriptionTranslate from './components/FeatureDescriptionTranslate'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import ExpandableSection from './components/ExpandableSection'
import Collection from './components/Collection'
import connectPropTypes from './utilities/connectPropTypes'
import colourSchema from './utilities/Data/Colour'
import getField from './utilities/Data/getField'
import postSchema from './utilities/Data/Post'
import ItemSchema from './utilities/Data/Item'
import urlForItem from './utilities/urlForItem'
import displayModes from './enums/displayModes'
import Colour from './components/Item/Colour'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'

const getUidFromUser = user => (user ? user.uid : null)

//	Grabs the images, optionally filtering by colour name
const getImageMap = (item, colourName) => {
	const canonicalColourName = colourSchema.canonicalName(colourName)
	const canonicalAllColours = colourSchema.canonicalName(ItemSchema.allColours)
	let imageMap = (item && item.colours ? Object.keys(item.colours)
		.map(idx => item.colours[idx])
		.filter(colour => !colourName || canonicalColourName === canonicalAllColours || (colour.colourName && colourSchema.canonicalName(colour.colourName) === canonicalColourName))
		.map(colour => ({ colourName: colour.colourName, images: colour.images }))
		.filter(colourMap => colourMap.images && colourMap.images.length) : [])
	if (!imageMap || imageMap.length === 0) {
		return []
	}

	if (!colourName || canonicalColourName === canonicalAllColours) {
		return imageMap
	}

	const output = []
	const iColourName = imageMap[0].colourName
	const allImages = imageMap[0].images
	let hasSegments = false

	//	This creates arrays of arrays, segmented into colours if needed
	let index = 0
	if (allImages && allImages.length > 0) {
		allImages.forEach((img) => {
			output[index] = output[index] || {}
			output[index].images = output[index].images || []
			output[index].images.push(img)
			output[index].colourName = iColourName
			if (img.type === 'twentieth') {
				index += 1
				hasSegments = true
			}
		})
	}

	if (hasSegments && index > 1) {
		imageMap = output
	}

	return imageMap
}

const urlForTagSearch = tag => `/search/?query=${encodeURIComponent(tag)}`

class Item extends React.Component {
	static getDerivedStateFromProps(props, prevState) {
		const parsedSearch = getNormalisedParameters(props.location.search)
		const itemKey = props && props.match && props.match.params ?
			props.match.params.itemKey :
			null

		const colour = getDecodedURIComponent(parsedSearch.colour || props.match.params.colour || ItemSchema.allColours)
		let imageMap = prevState.imageMap
		let viewingColour = prevState.viewingColour
		let images = prevState.images
		if (props.item && props.item.itemKey === itemKey && (!imageMap || colour !== prevState.colour)) {
			imageMap = getImageMap(props.item, colour)
			images = imageMap.map(entry => entry.images)
			viewingColour = (imageMap.length && imageMap[0].colourName) || (props.item && props.item.colours && props.item.colours.length && props.item.colours[0].colourName) || undefined
		}

		return {
			itemKey,
			colour,
			size: getDecodedURIComponent(parsedSearch.size || props.match.params.size),
			imageMap,
			images,
			viewingColour
		}
	}

	constructor(props) {
		super(props)
		this.state = {}

		this.handleColourChange = this.handleColourChange.bind(this)
		this.handleSizeChange = this.handleSizeChange.bind(this)
		this.onVoteBinder = this.onVoteBinder.bind(this)
		this.fetchData = this.fetchData.bind(this)
		this.bindHandleClickTag = this.bindHandleClickTag.bind(this)
		this.handleOwnSet = this.handleOwnSet.bind(this)
		this.handleWantSet = this.handleWantSet.bind(this)
	}

	componentDidMount() {
		this.fetchData()
	}

	componentDidUpdate(prevProps, prevState) {
		const itemName = this.props.match && this.props.match.params ?
			this.props.match.params.itemName :
			null

		if (prevProps && prevProps.item !== this.props.item && this.props.user.isAdmin) {
			this.props.appAddContextMenu({
				func: () => {
					const item = this.props.item
					const itemDraft = {
						itemKey: this.state.itemKey,
						url: item.url,
						brandKey: item.brandKey,
						brandName: item.brandName,
						itemName: item.itemName,
						gender: item.gender,
						itemType: item.itemType,
						colour: this.state.viewingColour || (item.colours && item.colours.length > 0 && item.colours[0].colourName),
						colours: item.colours,
						description: item.description,
						category: item.category,
						sizing: item.sizing
					}
					let colour = item.colours && Object.keys(item.colours).filter(key => item.colours[key].colourName === itemDraft.colour)
					colour = colour && item.colours[colour]
					//	Always picks the original image
					const image = colour && colour.images && colour.images.length && colour.images[0].url

					itemDraft.image = image
					this.props.itemEdit({ itemDraft })
					this.props.appRedirect('/item/edit')
				},
				text: this.context.translate('Edit item')
			})
		}

		if (prevState.itemKey !== this.state.itemKey) {
			this.fetchData()
		} else if (!itemName) {
			if (this.props.item) {
				const newUrl = urlForItem({ ...this.props.item, ...this.state })
				if (newUrl.indexOf(global.location.pathname) !== 0) {
					this.props.history.replace(newUrl)
				}
			}
		}
	}

	onVoteBinder(itemKey, featureType) {
		return (vote, prevVote) => {
			this.props.itemFeatureVote(featureType, vote, prevVote)
			this.props.itemFeatureVoteSave(getUidFromUser(this.props.user), vote.type, itemKey, featureType)
		}
	}

	handleOwnSet(own) {
		const item = (this.props.item && this.props.item.item === this.state.itemKey && this.props.item) || { itemKey: this.state.itemKey }
		this.props.itemOwnSet({ own, item: { ...item, colourName: this.state.colour === ItemSchema.allColours ? this.state.viewingColour : this.state.colour } })
	}

	handleWantSet(want) {
		const item = (this.props.item && this.props.item.item === this.state.itemKey && this.props.item) || { itemKey: this.state.itemKey }
		this.props.itemWantSet({ want, item: { ...item, colourName: this.state.colour === ItemSchema.allColours ? this.state.viewingColour : this.state.colour } })
	}

	fetchData() {
		const itemKey = this.state.itemKey
		if (itemKey) {
			this.props.itemFetch({ itemKey })
		} else {
			this.context.message(messageTypes.ERROR, this.context.translate('Item key not specified', { itemKey }), errorTypes.KEYNOTSPECIFIED)
		}
	}

	handleColourChange(e) {
		this.props.appRedirect(urlForItem({ ...this.props.item, ...this.state, colour: { colourName: e.target.value } }))
	}

	handleSizeChange(size) {
		this.props.appRedirect(urlForItem({ ...this.props.item, ...this.state, size, colour: { colourName: this.state.viewingColour } }))
	}

	bindHandleClickTag(tag) {
		return () => {
			this.props.appRedirect(urlForTagSearch(tag))
		}
	}

	render() {
		const { item, brand, displayFeatures, itemPosts, userVotes, itemOwnByKey, itemWantByKey } = this.props
		const hideBrandHeading = brand ? brand.logoContainsName : false

		return (
			<div className={styles.itemWrapper}>
				{item && (
					<div>
						<section className={classNames(styles.pagePadding, styles.header)}>
							<span className={styles.brandLogo}>
								{brand && (
									<Img
										className={styles.brandLogoImage}
										content={brand.logo}
										sizeType="brands"
									/>
								)}
							</span>
							{!hideBrandHeading && (
								<span className={styles.brandHeading}>
									<h2 className={styles.brandHeadingText}>
										{item.brandName}
									</h2>
								</span>
							)}
							<div className={styles.headerDetails}>
								<HeaderStatistic count={getField(item, 'countPosts')} name={this.context.translate('POSTS')} />
								<HeaderStatistic count={getField(item, 'countKits')} name={this.context.translate('KITS')} />
								<HeaderStatistic count={getField(item, 'countOwned')} name={this.context.translate('OWN')} />
								<HeaderStatistic count={getField(item, 'countWanted')} name={this.context.translate('WANT')} />
							</div>
						</section>

						<section>
							{item && item.colours && (
								<ImageCarousel
									images={this.state.images}
									onChange={idx => this.setState(state => ({ viewingColour: state.imageMap[idx].colourName }))}
								/>
							)}
							{item && !item.colours &&
								<p>{this.context.translate('No images found')}{/* TODO: Handle this somehow... */}</p>
							}
						</section>

						<section className={`${styles.pagePadding} ${styles.ownWantContainer}`}>
							<OwnWant
								own={ItemSchema.isOwned(itemOwnByKey)(item, this.state.colour === ItemSchema.allColours ? this.state.viewingColour : this.state.colour)}
								want={ItemSchema.isWanted(itemWantByKey)(item, this.state.colour === ItemSchema.allColours ? this.state.viewingColour : this.state.colour)}
								countOwned={item.countOwned}
								countWanted={item.countWanted}
								ownSet={this.handleOwnSet}
								wantSet={this.handleWantSet}
								buyUrl={this.props.item.url}
								buyItemKey={item.itemKey}
								buyBrand={item.brandKey}
								//	TODO: remove title, as it is only used for fake KF score
								title={item.itemKey}
								showFits
							/>
						</section>

						<Colour
							colourName={this.state.colour}
							colourNames={item && item.colours ? Object.keys(item.colours).map(key => item.colours[key].colourName || ItemSchema.allColoursText) : [ItemSchema.allColoursText]}
							handleColourChange={this.handleColourChange}
						/>

						<section>
							<span className={styles.selectSurround}>
								<SizingSelect
									classes={{ root: styles.sizingSelect }}
									size={this.state.size}
									handleSizeChange={this.handleSizeChange}
									allSizesValue={ItemSchema.allSizes}
									sizing={item.sizing}
									gender={item.gender}
									itemKey={item.itemKey}
								/>
							</span>
						</section>

						{itemPosts && itemPosts.length > 0 && (
							<ExpandableSection title={itemPosts ? this.context.translate('{posts|# - Activity Post|# - Activity Posts}', { posts: item.countPosts || 0 }) : this.context.translate('Activity Posts')} defaultExpanded>
								<Collection
									items={itemPosts}
									{...postSchema.collectionProps}
									allowedDisplayModes={[displayModes.GRID.type, displayModes.LIST.type]}
									defaultDisplayMode={displayModes.GRID.type}
									itemOnSelect={post => this.props.appRedirect(postSchema.collectionProps.itemHref(post))}
									renderWhenNoItems={postSchema.renderNoPosts(!!itemPosts, undefined, this.context.translate('No posts'))}
									itemShowOwnWant={false}
								/>
							</ExpandableSection>
						)}

						<section className={styles.pagePadding}>
							<h2 className={styles.itemHeadingText}>{this.context.translate('{brandName} {itemName}', item)}</h2>
							{item.description && (
								<ClampedText className={styles.description}>
									{item.description}
								</ClampedText>
							)}
						</section>

						<ExpandableSection title={this.context.translate('Features')} defaultExpanded>
							<div className={styles.pagePadding}>
								{displayFeatures && (
									<p className={styles.featureText}>
										{this.context.translate('These are the best features - agree/disagree? Vote now!')}
									</p>
								)}
								{displayFeatures && displayFeatures.map((feature, idx) => {
									const tipTitle = (
										<FeatureDescriptionTranslate
											type={feature.type}
											subTypes={feature.subTypes}
										/>
									)

									return (
										// eslint-disable-next-line react/no-array-index-key
										<div className={styles.feature} key={`feature${item.itemKey}-${idx}`}>
											{/* Tooltip is only shown (via the classes attribute) if we have minimum info */}
											<Tooltip
												title={tipTitle}
												classes={feature.type && feature.subTypes && feature.subTypes.length > 0 ? {} : { popper: styles.hideTooltipPopper }}
											>
												<span>
													<FeatureTranslate type={feature.type} />
												</span>
											</Tooltip>
											<span className={styles.featureIcons}>
												<UpDownVote
													vote={userVotes[feature.type] ?
														voteTypes[userVotes[feature.type].voteType] :
														undefined}
													onVoteChange={this.onVoteBinder(item.itemKey, feature.type)}
													score={feature.score}
												/>
											</span>
										</div>
									)
								})
								}
								{!displayFeatures && (
									<div>
										<p>{this.context.translate('No features found')}</p>
									</div>
								)}
							</div>
						</ExpandableSection>

						<ExpandableSection
							title={this.context.translate('Tags')}
							defaultExpanded
						>
							<Collection
								items={item.category}
								classes={{
									container: styles.pagePadding,
									itemsContainer: styles.tagContainer
								}}
							>
								{props => (
									<Button key={props.index} variant="text" onClick={this.bindHandleClickTag(props.item)} className={styles.tagItem}>
										{props.item}
									</Button>
								)}
							</Collection>
						</ExpandableSection>

						<div className={styles.paddy} />
					</div>
				)
				}
			</div>
		)
	}
}

Item.propTypes = {
	item: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemPosts: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	brand: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	user: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
	displayFeatures: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	userVotes: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemEdit: PropTypes.func.isRequired,
	itemFetch: PropTypes.func.isRequired,
	itemFeatureVote: PropTypes.func.isRequired,
	itemFeatureVoteSave: PropTypes.func.isRequired,
	itemOwnByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemWantByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemOwnSet: PropTypes.func.isRequired,
	itemWantSet: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appAddContextMenu: PropTypes.func.isRequired,
	history: PropTypes.shape({
		push: PropTypes.func.isRequired,
		replace: PropTypes.func.isRequired
	}),
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired
}

Item.defaultProps = {
	item: undefined,
	itemOwnByKey: undefined,
	itemWantByKey: undefined,
	itemPosts: undefined,
	brand: undefined,
	displayFeatures: undefined,
	userVotes: {},
	history: { push: () => { /* ERROR */ }, replace: () => { /* ERROR */ } }
}

Item.contextTypes = {
	translate: PropTypes.func,
	message: PropTypes.func
}

export const testing = {
	Item
}

export default connectPropTypes(Item)



// WEBPACK FOOTER //
// ./src/Item.jsx